// MobileEditorToolbar.jsx - Updated with integrated fullscreen button
import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Bold,
  Italic,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link,
  Unlink,
  Undo,
  Redo,
  Type,
  Palette,
  Menu,
  X,
  ChevronDown,
  Maximize2,
  Minimize2,
} from "lucide-react";

const MobileEditorToolbar = ({
  editor,
  selectionKey,
  onToggleFullscreen,
  isFullscreen,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const executeCommand = useCallback(
    (commandFn) => {
      return (e) => {
        e.preventDefault();
        e.stopPropagation();

        const scrollY = window.scrollY;
        const scrollX = window.scrollX;

        requestAnimationFrame(() => {
          if (!editor || editor.isDestroyed) return;
          commandFn();
          window.scrollTo(scrollX, scrollY);
        });
      };
    },
    [editor]
  );

  const fontSizeOptions = [
    { label: "8pt", value: "8pt" },
    { label: "10pt", value: "10pt" },
    { label: "11pt", value: "11pt" },
    { label: "12pt", value: "12pt" },
    { label: "14pt", value: "14pt" },
    { label: "16pt", value: "16pt" },
    { label: "18pt", value: "18pt" },
    { label: "20pt", value: "20pt" },
  ];

  const headingOptions = [
    { label: "Normal", value: "p" },
    { label: "H1", value: "h1" },
    { label: "H3", value: "h3" },
  ];

  const colorPresets = [
    { name: "Default", value: "" },
    { name: "Black", value: "#000000" },
    { name: "Gray", value: "#374151" },
    { name: "Blue", value: "#1971c2" },
    { name: "Green", value: "#065f46" },
    { name: "Red", value: "#dc2626" },
  ];

  const getCurrentFontSize = () => {
    const fontSize = editor.getAttributes("textStyle").fontSize;
    return fontSize || "11pt";
  };

  const getHeadingLevel = () => {
    if (editor.isActive("heading", { level: 1 })) return "h1";
    if (editor.isActive("heading", { level: 3 })) return "h3";
    return "p";
  };

  const getCurrentColor = () => {
    return editor.getAttributes("textStyle").color || "";
  };

  const setFontSize = useCallback(
    (size) => {
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      requestAnimationFrame(() => {
        if (!editor || editor.isDestroyed) return;
        if (size) {
          editor.commands.setFontSize(size);
        } else {
          editor.commands.unsetFontSize();
        }
        window.scrollTo(scrollX, scrollY);
      });
    },
    [editor]
  );

  const setHeadingLevel = useCallback(
    (level) => {
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      requestAnimationFrame(() => {
        if (!editor || editor.isDestroyed) return;
        if (level === "h1") {
          editor.commands.setHeading({ level: 1 });
        } else if (level === "h3") {
          editor.commands.setHeading({ level: 3 });
        } else {
          editor.commands.setParagraph();
        }
        window.scrollTo(scrollX, scrollY);
      });
    },
    [editor]
  );

  const setColor = useCallback(
    (color) => {
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      requestAnimationFrame(() => {
        if (!editor || editor.isDestroyed) return;
        if (color) {
          editor.commands.setColor(color);
        } else {
          editor.commands.unsetColor();
        }
        window.scrollTo(scrollX, scrollY);
      });
    },
    [editor]
  );

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href || "";
    const url = window.prompt("Enter URL", previousUrl);

    if (url === null) return;

    if (url === "") {
      editor.chain().extendMarkRange("link").unsetLink().run();
      return;
    }

    if (
      !url.startsWith("http://") &&
      !url.startsWith("https://") &&
      !url.startsWith("mailto:")
    ) {
      alert("Please enter a valid URL");
      return;
    }

    editor.chain().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="mobile-editor-toolbar">
      {/* Collapsed Toolbar */}
      <div className="mobile-toolbar-collapsed">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mobile-menu-toggle"
        >
          {isExpanded ? <X size={20} /> : <Menu size={20} />}
        </button>

        {/* Quick Actions - Always Visible */}
        <div className="mobile-quick-actions">
          <button
            onMouseDown={executeCommand(() =>
              editor.chain().toggleBold().run()
            )}
            className={`mobile-quick-btn ${
              editor.isActive("bold") ? "active" : ""
            }`}
          >
            <Bold size={16} />
          </button>
          <button
            onMouseDown={executeCommand(() =>
              editor.chain().toggleItalic().run()
            )}
            className={`mobile-quick-btn ${
              editor.isActive("italic") ? "active" : ""
            }`}
          >
            <Italic size={16} />
          </button>
          <button
            onMouseDown={executeCommand(() =>
              editor.chain().toggleBulletList().run()
            )}
            className={`mobile-quick-btn ${
              editor.isActive("bulletList") ? "active" : ""
            }`}
          >
            <List size={16} />
          </button>
          <button
            onMouseDown={executeCommand(() => editor.chain().undo().run())}
            className="mobile-quick-btn"
            disabled={!editor.can().undo()}
          >
            <Undo size={16} />
          </button>
          <button
            onMouseDown={executeCommand(() => editor.chain().redo().run())}
            className="mobile-quick-btn"
            disabled={!editor.can().redo()}
          >
            <Redo size={16} />
          </button>
        </div>

        {/* Fullscreen Toggle Button - Integrated into toolbar */}
        <button
          onClick={onToggleFullscreen}
          className="mobile-toolbar-fullscreen-btn"
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
        </button>
      </div>

      {/* Expanded Toolbar */}
      {isExpanded && (
        <div className="mobile-toolbar-expanded">
          {/* Text Style Row */}
          <div className="mobile-toolbar-row">
            <div className="mobile-dropdown-group">
              <button
                onClick={() =>
                  setActiveDropdown(
                    activeDropdown === "heading" ? null : "heading"
                  )
                }
                className="mobile-dropdown-btn"
              >
                <Type size={14} />
                <span>
                  {
                    headingOptions.find((h) => h.value === getHeadingLevel())
                      ?.label
                  }
                </span>
                <ChevronDown size={14} />
              </button>
              {activeDropdown === "heading" && (
                <div className="mobile-dropdown-menu">
                  {headingOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setHeadingLevel(option.value);
                        setActiveDropdown(null);
                      }}
                      className={`mobile-dropdown-item ${
                        getHeadingLevel() === option.value ? "active" : ""
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mobile-dropdown-group">
              <button
                onClick={() =>
                  setActiveDropdown(
                    activeDropdown === "fontSize" ? null : "fontSize"
                  )
                }
                className="mobile-dropdown-btn"
              >
                <span className="font-size-icon">Aa</span>
                <span>{getCurrentFontSize()}</span>
                <ChevronDown size={14} />
              </button>
              {activeDropdown === "fontSize" && (
                <div className="mobile-dropdown-menu">
                  {fontSizeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setFontSize(option.value);
                        setActiveDropdown(null);
                      }}
                      className={`mobile-dropdown-item ${
                        getCurrentFontSize() === option.value ? "active" : ""
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mobile-dropdown-group">
              <button
                onClick={() =>
                  setActiveDropdown(activeDropdown === "color" ? null : "color")
                }
                className="mobile-dropdown-btn"
              >
                <Palette size={14} />
                <span>
                  {colorPresets.find((c) => c.value === getCurrentColor())
                    ?.name || "Color"}
                </span>
                <ChevronDown size={14} />
              </button>
              {activeDropdown === "color" && (
                <div className="mobile-dropdown-menu">
                  {colorPresets.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => {
                        setColor(color.value);
                        setActiveDropdown(null);
                      }}
                      className={`mobile-dropdown-item ${
                        getCurrentColor() === color.value ? "active" : ""
                      }`}
                    >
                      <span
                        className="color-preview"
                        style={{
                          backgroundColor: color.value || "#000",
                          width: "16px",
                          height: "16px",
                          borderRadius: "2px",
                          display: "inline-block",
                          marginRight: "8px",
                          border: "1px solid #ddd",
                        }}
                      />
                      {color.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Alignment & List Row */}
          <div className="mobile-toolbar-row">
            <div className="mobile-btn-group">
              <button
                onMouseDown={executeCommand(() =>
                  editor.chain().setTextAlign("left").run()
                )}
                className={`mobile-toolbar-btn ${
                  editor.isActive({ textAlign: "left" }) ? "active" : ""
                }`}
              >
                <AlignLeft size={16} />
              </button>
              <button
                onMouseDown={executeCommand(() =>
                  editor.chain().setTextAlign("center").run()
                )}
                className={`mobile-toolbar-btn ${
                  editor.isActive({ textAlign: "center" }) ? "active" : ""
                }`}
              >
                <AlignCenter size={16} />
              </button>
              <button
                onMouseDown={executeCommand(() =>
                  editor.chain().setTextAlign("right").run()
                )}
                className={`mobile-toolbar-btn ${
                  editor.isActive({ textAlign: "right" }) ? "active" : ""
                }`}
              >
                <AlignRight size={16} />
              </button>
            </div>

            <div className="mobile-btn-group">
              <button
                onMouseDown={executeCommand(() =>
                  editor.chain().toggleOrderedList().run()
                )}
                className={`mobile-toolbar-btn ${
                  editor.isActive("orderedList") ? "active" : ""
                }`}
              >
                <ListOrdered size={16} />
              </button>
              <button
                onMouseDown={executeCommand(setLink)}
                className={`mobile-toolbar-btn ${
                  editor.isActive("link") ? "active" : ""
                }`}
              >
                <Link size={16} />
              </button>
              <button
                onMouseDown={executeCommand(() => editor.commands.unsetLink())}
                className="mobile-toolbar-btn"
                disabled={!editor.isActive("link")}
              >
                <Unlink size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileEditorToolbar;
